import React, { Suspense, lazy } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { ThemeProvider } from "./context/ThemeContext";
import { AdminProvider } from "./context/AdminContext";
import ChatWidget from "./components/ChatWidget";
import CookieConsentBanner from "./components/CookieConsentBanner";
import MobileCTABar from "./components/mobile/MobileCTABar";
import ScrollToTop from "./components/ScrollToTop";
import ExitIntentPopup from "./components/ExitIntentPopup";

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Handle marketing signup from exit popup
const handleMarketingSignup = async (data) => {
  try {
    const response = await fetch(`${API_URL}/api/marketing-signup`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email: data.email || '',
        phone: data.phone || '',
        source: data.source || 'exit_popup',
        page_url: window.location.href
      })
    });
    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Marketing signup error:', error);
    return { success: false };
  }
};

// Critical pages - load immediately
import LandingPage from "./pages/LandingPage";
import HomePage from "./pages/HomePage";
import FencingPage from "./pages/FencingPage";

// Lazy load non-critical pages for better performance
const PropertyServicesPage = lazy(() => import("./pages/PropertyServicesPage"));
const PropertyServicePage = lazy(() => import("./pages/services/PropertyServicePage"));
const FenceServicePage = lazy(() => import("./pages/services/FenceServicePage"));
const VinylFencePage = lazy(() => import("./pages/services/VinylFencePage"));
const WoodFencePage = lazy(() => import("./pages/services/WoodFencePage"));
const ChainLinkFencePage = lazy(() => import("./pages/services/ChainLinkFencePage"));
const OrnamentalFencePage = lazy(() => import("./pages/services/OrnamentalFencePage"));
const FenceOnlineStore = lazy(() => import("./pages/shop/FenceOnlineStore"));
const FenceCalculator = lazy(() => import("./pages/FenceCalculator"));
const ContractorsPage = lazy(() => import("./pages/ContractorsPage"));
const TradeAccountPage = lazy(() => import("./pages/TradeAccountPage"));
const InstallationPage = lazy(() => import("./pages/InstallationPage"));
const ComingSoonPage = lazy(() => import("./pages/ComingSoonPage"));
const CRMRedirect = lazy(() => import("./pages/CRMRedirect"));
const AdminLogin = lazy(() => import("./pages/admin/AdminLogin"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const FenceResidentialTerms = lazy(() => import("./pages/terms/FenceResidentialTerms"));
const FenceCommercialTerms = lazy(() => import("./pages/terms/FenceCommercialTerms"));
const FenceGCTerms = lazy(() => import("./pages/terms/FenceGCTerms"));
const PropertyServicesTerms = lazy(() => import("./pages/terms/PropertyServicesTerms"));
// Bylaws & Resources Pages
const BylawsPage = lazy(() => import("./pages/bylaws/BylawsPage"));
const MunicipalityBylawPage = lazy(() => import("./pages/bylaws/MunicipalityBylawPage"));
const SafeDiggingPage = lazy(() => import("./pages/bylaws/SafeDiggingPage"));
const FeaturedBuildersPage = lazy(() => import("./pages/bylaws/FeaturedBuildersPage"));
// New Pages
const AboutPage = lazy(() => import("./pages/AboutPage"));
const GalleryPage = lazy(() => import("./pages/GalleryPage"));
const ServiceAreaPage = lazy(() => import("./pages/ServiceAreaPage"));
const BlogPage = lazy(() => import("./pages/BlogPage"));
const BlogArticlePage = lazy(() => import("./pages/BlogArticlePage"));
const WarrantyPage = lazy(() => import("./pages/WarrantyPage"));
const WarrantyClaimPage = lazy(() => import("./pages/WarrantyClaimPage"));
const FinancingPage = lazy(() => import("./pages/FinancingPage"));
const PaymentsPage = lazy(() => import("./pages/PaymentsPage"));
// New Marketing Pages
const SpecSheetsPage = lazy(() => import("./pages/SpecSheetsPage"));
const CaseStudiesPage = lazy(() => import("./pages/CaseStudiesPage"));
const ReferralProgramPage = lazy(() => import("./pages/ReferralProgramPage"));
const ContractorResourcesPage = lazy(() => import("./pages/ContractorResourcesPage"));
const FenceConfiguratorAI = lazy(() => import("./pages/FenceConfiguratorAI"));
const FAQPage = lazy(() => import("./pages/FAQPage"));
const CityLandingPage = lazy(() => import("./pages/services/CityLandingPage"));
const DevelopmentsPage = lazy(() => import("./pages/DevelopmentsPage"));
// PS Pages
const PSGalleryPage = lazy(() => import("./pages/ps/PSGalleryPage"));
const PSAboutPage = lazy(() => import("./pages/ps/PSAboutPage"));
const PSServiceAreaPage = lazy(() => import("./pages/ps/PSServiceAreaPage"));
const PSFAQPage = lazy(() => import("./pages/ps/PSFAQPage"));
const PSBlogPage = lazy(() => import("./pages/ps/PSBlogPage"));
const PSBlogArticlePage = lazy(() => import("./pages/ps/PSBlogArticlePage"));
const PSPaymentsPage = lazy(() => import("./pages/ps/PSPaymentsPage"));
const LinePaintingPage = lazy(() => import("./pages/ps/LinePaintingPage"));
// Contact Pages
const FenceContactPage = lazy(() => import("./pages/contact/FenceContactPage"));
const PSContactPage = lazy(() => import("./pages/contact/PSContactPage"));
// Legal Pages
const PrivacyPolicyPage = lazy(() => import("./pages/PrivacyPolicyPage"));
const TermsOfServicePage = lazy(() => import("./pages/TermsOfServicePage"));
const CookiePolicyPage = lazy(() => import("./pages/CookiePolicyPage"));
const AdminPage = lazy(() => import("./pages/AdminPage"));

// Loading component for Suspense fallback
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
    <div className="text-center">
      <div className="w-12 h-12 border-4 border-[#4A7C9B] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
      <p className="text-gray-600 dark:text-gray-400">Loading...</p>
    </div>
  </div>
);

function App() {
  return (
    <HelmetProvider>
      <ThemeProvider>
        <AdminProvider>
          <div className="App">
            <BrowserRouter>
            <ScrollToTop />
            <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Main Landing Page - Choose Fence or Property Services */}
              <Route path="/" element={<HomePage />} />
              
              {/* Property Services Section */}
              <Route path="/ps" element={<PropertyServicesPage />} />
              <Route path="/ps/gallery" element={<PSGalleryPage />} />
              <Route path="/ps/about" element={<PSAboutPage />} />
              <Route path="/ps/service-area" element={<PSServiceAreaPage />} />
              <Route path="/ps/faq" element={<PSFAQPage />} />
              <Route path="/ps/blog" element={<PSBlogPage />} />
              <Route path="/ps/blog/:slug" element={<PSBlogArticlePage />} />
              <Route path="/ps/payments" element={<PSPaymentsPage />} />
              <Route path="/ps/line-painting" element={<LinePaintingPage />} />
              <Route path="/ps/terms" element={<PropertyServicesTerms />} />
              <Route path="/ps/contact" element={<PSContactPage />} />
              <Route path="/ps/:slug" element={<PropertyServicePage />} />
              
              {/* Fencing Section - All fence pages under /fence/ */}
              <Route path="/fence" element={<FencingPage />} />
              <Route path="/fence/about" element={<AboutPage />} />
              <Route path="/fence/contact" element={<FenceContactPage />} />
              <Route path="/fence/gallery" element={<GalleryPage />} />
              <Route path="/fence/service-area" element={<ServiceAreaPage />} />
              <Route path="/fence/blog" element={<BlogPage />} />
              <Route path="/fence/blog/:articleId" element={<BlogArticlePage />} />
              <Route path="/fence/warranty" element={<WarrantyPage />} />
              <Route path="/fence/warranty/claim" element={<WarrantyClaimPage />} />
              <Route path="/fence/financing" element={<FinancingPage />} />
              <Route path="/fence/payments" element={<PaymentsPage />} />
              <Route path="/fence/specs" element={<SpecSheetsPage />} />
              <Route path="/fence/case-studies" element={<CaseStudiesPage />} />
              <Route path="/fence/referrals" element={<ReferralProgramPage />} />
              <Route path="/fence/resources" element={<ContractorResourcesPage />} />
              <Route path="/fence/configurator" element={<FenceConfiguratorAI />} />
              <Route path="/fence/calculator" element={<FenceCalculator />} />
              <Route path="/fence/faq" element={<FAQPage />} />
              <Route path="/fence/contractors" element={<ContractorsPage />} />
              <Route path="/fence/trade-account" element={<TradeAccountPage />} />
              <Route path="/fence/installation" element={<InstallationPage />} />
              <Route path="/fence/store" element={<FenceOnlineStore />} />
              <Route path="/fence/vinyl" element={<VinylFencePage />} />
              <Route path="/fence/wood" element={<WoodFencePage />} />
              <Route path="/fence/chain-link" element={<ChainLinkFencePage />} />
              <Route path="/fence/ornamental" element={<OrnamentalFencePage />} />
              <Route path="/fence/terms/residential" element={<FenceResidentialTerms />} />
              <Route path="/fence/terms/commercial" element={<FenceCommercialTerms />} />
              <Route path="/fence/terms/gc" element={<FenceGCTerms />} />
              
              {/* Bylaws & Resources */}
              <Route path="/fence/bylaws" element={<BylawsPage />} />
              <Route path="/fence/bylaws/safe-digging" element={<SafeDiggingPage />} />
              <Route path="/fence/bylaws/featured-builders" element={<FeaturedBuildersPage />} />
              <Route path="/fence/bylaws/:municipalityId" element={<MunicipalityBylawPage />} />
              
              {/* City-Specific Landing Pages (for Local SEO) */}
              <Route path="/fence/red-deer" element={<CityLandingPage />} />
              <Route path="/fence/lacombe" element={<CityLandingPage />} />
              <Route path="/fence/sylvan-lake" element={<CityLandingPage />} />
              <Route path="/fence/blackfalds" element={<CityLandingPage />} />
              <Route path="/fence/ponoka" element={<CityLandingPage />} />
              <Route path="/fence/innisfail" element={<CityLandingPage />} />
              <Route path="/fence/penhold" element={<CityLandingPage />} />
              <Route path="/fence/rocky-mountain-house" element={<CityLandingPage />} />
              
              {/* New Developments Page */}
              <Route path="/fence/developments" element={<DevelopmentsPage />} />
              
              {/* Fence service detail page (must be last in /fence/ routes) */}
              <Route path="/fence/:slug" element={<FenceServicePage />} />
              
              {/* Legal Pages */}
              <Route path="/privacy" element={<PrivacyPolicyPage />} />
              <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
              <Route path="/terms" element={<TermsOfServicePage />} />
              <Route path="/terms-of-service" element={<TermsOfServicePage />} />
              <Route path="/cookies" element={<CookiePolicyPage />} />
              <Route path="/cookie" element={<CookiePolicyPage />} />
              <Route path="/cookie-policy" element={<CookiePolicyPage />} />
              
              {/* CRM Portal Redirect */}
              <Route path="/crm" element={<CRMRedirect />} />
              
              {/* Admin Pages */}
              <Route path="/admin" element={<AdminLogin />} />
              <Route path="/admin/dashboard" element={<AdminDashboard />} />
              <Route path="/admin/content" element={<AdminPage />} />
              
              {/* Catch-all for any other routes */}
              <Route path="*" element={<ComingSoonPage />} />
            </Routes>
            </Suspense>
            <ChatWidget />
            <CookieConsentBanner />
            <MobileCTABar />
            <ExitIntentPopup primaryColor="#456CAC" onSubmit={handleMarketingSignup} />
            </BrowserRouter>
          </div>
        </AdminProvider>
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
